import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  DatePicker,
  MultiEmailInputField,
  RangePicker,
  Switch,
  TimePicker,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import {
  TextWithIcon,
  Typography,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import componentConstants from '../../../core-utils/messages/componentConstants';
import {
  EmailSchedulerErrorType,
  EmailSchedulerType,
  emailSchedulerDefaultObject,
  emailSchedulerErrorDefaultObject,
  getDayDropdownOptions,
  getDayDropdownValues,
} from '../../../core-utils/helper-utils/emailSchedulerHelper';
import moment from 'moment';
import { useStores } from '../../../stores';

export interface EmailSchedulerProps {
  isCustomType: boolean;
  frequencyType: string;
  emailSchedulerData: EmailSchedulerType;
  setEmailSchedulerData: React.Dispatch<
    React.SetStateAction<EmailSchedulerType>
  >;
  emailSchedulerError: EmailSchedulerErrorType;
  setEmailSchedulerError: React.Dispatch<
    React.SetStateAction<EmailSchedulerErrorType>
  >;
  showInfoText?: boolean;
  checked: boolean;
  setChecked: React.Dispatch<React.SetStateAction<boolean>>;
}

const EmailScheduler: React.FC<EmailSchedulerProps> = ({
  isCustomType,
  frequencyType,
  emailSchedulerData,
  setEmailSchedulerData,
  emailSchedulerError,
  setEmailSchedulerError,
  showInfoText,
  checked,
  setChecked,
}) => {
  const useStyles = makeStyles()({
    root: {
      marginTop: '16px',
    },
    titleGrid: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    editDurationGrid: {
      marginTop: '16px',
    },
    inputFieldsGrid: {
      display: 'flex',
      marginTop: '16px',
      marginBottom: '16px',
    },
    textGrid: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'break-spaces',
      marginTop: '16px',
    },
    tooltip: {
      padding: '10px',
      minWidth: '357px',
      MaxWidth: '424px',
      backgroundColor: patientAccessTheme.palette.grey[900],
      borderRadius: '4px',
      fontSize: '12px',
      fontFamily: 'Inter',
      lineHeight: '160%',
    },
  });

  //constants
  const { classes } = useStyles();
  const constants = componentConstants.ORGANISMS.EMAIL_SCHEDULER;
  const customReportsStore = useStores().customReportsStore;

  //states
  const [startDateMinDate] = useState(moment());
  const [startDateMaxDate] = useState(moment().add(3, 'years'));
  const [dateHelperText, setDateHelperText] = React.useState('');
  const [timeHelperText, setTimeHelperText] = React.useState('');

  //functions
  const handleSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isEnabled = event.target.checked as boolean;
    setChecked(isEnabled);
    if (!isEnabled) {
      setEmailSchedulerData(emailSchedulerDefaultObject);
      setEmailSchedulerError(emailSchedulerErrorDefaultObject);
      setDateHelperText('');
      setTimeHelperText('');
    }
  };

  const validDate = (date: Date) => {
    if (!date || isNaN(date.getTime())) {
      setDateHelperText('Invalid date format');
      return false;
    }

    const currentDate = moment().startOf('day');
    const maxDate = moment().add(3, 'years');

    if (moment(date).startOf('day').isBefore(currentDate)) {
      setDateHelperText('Date should not be before current date');
      return false;
    }

    if (moment(date).isAfter(maxDate)) {
      setDateHelperText('Date should not be after maximal date');
      return false;
    }

    setDateHelperText('');
    return true;
  };

  const validTime = (time: Date) => {
    if (!time || isNaN(time.getTime())) {
      setTimeHelperText('Invalid time format');
      return false;
    }

    const currentDate = moment().startOf('day');
    const currentTime = moment().startOf('second');

    if (emailSchedulerData?.scheduledDate) {
      const selectedDate = emailSchedulerData.scheduledDate;
      const isPresentDate = moment(selectedDate).isSame(currentDate, 'day');

      if (isPresentDate && moment(time).isBefore(currentTime)) {
        setTimeHelperText('Time should not be in the past');
        return false;
      }
    }

    setTimeHelperText('');
    return true;
  };

  const validate = (name: string, value: any) => {
    switch (name) {
      case 'scheduledDate':
        return validDate(value);
      case 'scheduledTime':
        return validTime(value);
      default:
        return true;
    }
  };

  const handleInputChange = (value: any, name: string) => {
    setEmailSchedulerData((prevState) => ({ ...prevState, [name]: value }));

    const error = !validate(name, value);
    setEmailSchedulerError((prevState) => ({ ...prevState, [name]: error }));

    if (isCustomType && name === 'scheduledDate') {
      setEmailSchedulerData((prevState) => ({
        ...prevState,
        ['scheduledDay']: value?.getDate()?.toString() ?? '',
      }));
    }
  };

  //useEffect
  useEffect(() => {
    setDateHelperText('');
    setTimeHelperText('');
    if (
      !customReportsStore.isEdit ||
      frequencyType !== customReportsStore.downloadfrequency
    ) {
      setEmailSchedulerData(emailSchedulerDefaultObject);
    }
    setEmailSchedulerError(emailSchedulerErrorDefaultObject);
  }, [frequencyType]);

  const getInfoText = (label: string, isHighEmphasis: boolean) => {
    return (
      <Typography
        fontSize="12px"
        fontType="regular"
        lineHeight="21.24px"
        textColor={
          isHighEmphasis
            ? patientAccessTheme.palette.text.highemphasis
            : patientAccessTheme.palette.text.mediumemphasis
        }
      >
        {label}
      </Typography>
    );
  };
  return (
    <Grid className={classes.root}>
      <Grid className={classes.titleGrid}>
        <TextWithIcon
          label={constants.TITLE}
          endIcon={
            <Tooltip
              title={
                isCustomType
                  ? constants.CUSTOM_SELECTION_TOOLTIP
                  : constants.PERIODIC_SELECTION_TOOLTIP
              }
              classes={{ tooltip: classes.tooltip }}
              arrow
            >
              <div>
                <Info height="20px" width="20px" style={{ display: 'flex' }} />
              </div>
            </Tooltip>
          }
        />
        <Switch checked={checked} handleChange={handleSwitch} />
      </Grid>
      {checked && (
        <>
          {!isCustomType && (
            <Grid className={classes.editDurationGrid}>
              <RangePicker
                startDate={emailSchedulerData.schedulerStartDate}
                endDate={emailSchedulerData.schedulerEndDate}
                startDateMinDate={startDateMinDate}
                startDateMaxDate={startDateMaxDate}
                endDateMinDate={emailSchedulerData?.schedulerStartDate}
                endDateMaxDate={startDateMaxDate}
                handleStartDateChange={(date: Date) =>
                  handleInputChange(date, 'schedulerStartDate')
                }
                handleEndDateChange={(date: Date) =>
                  handleInputChange(date, 'schedulerEndDate')
                }
              />
            </Grid>
          )}
          <Grid className={classes.inputFieldsGrid}>
            {isCustomType ? (
              <ReportCreateNewLabelField
                label={constants.DATE_LABEL}
                component={
                  <DatePicker
                    width="301px"
                    selectedDate={emailSchedulerData?.scheduledDate}
                    handleChange={(date: Date) =>
                      handleInputChange(date, 'scheduledDate')
                    }
                    minDate={moment()}
                    maxDate={moment().add(3, 'years')}
                    placeholder={constants.DATE_PLACEHOLDER}
                    error={emailSchedulerError?.scheduledDate}
                    helperText={dateHelperText}
                  />
                }
                marginRight="13px"
                marginBottom="0px"
              />
            ) : (
              <ReportCreateNewLabelField
                label={constants.DAY_LABEL}
                component={
                  <Dropdown
                    label={constants.DAY_PLACEHOLDER}
                    dropDownOptions={getDayDropdownOptions(
                      moment(
                        emailSchedulerData?.schedulerStartDate,
                      ).daysInMonth(),
                    )}
                    dropDownValues={getDayDropdownValues(
                      moment(
                        emailSchedulerData?.schedulerStartDate,
                      ).daysInMonth(),
                    )}
                    value={emailSchedulerData?.scheduledDay}
                    setValue={null}
                    handleChange={(event: any) => {
                      handleInputChange(event.target.value, 'scheduledDay');
                    }}
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '301px',
                    }}
                    labelTop="10px"
                    MenuProps={{
                      style: {
                        height: '400px',
                        width: '301px',
                      },
                    }}
                  />
                }
                marginRight="13px"
                marginBottom="0px"
              />
            )}
            <ReportCreateNewLabelField
              label={constants.TIME_LABEL}
              component={
                <TimePicker
                  width="301px"
                  selectedTime={emailSchedulerData?.scheduledTime}
                  handleChange={(value: any) =>
                    handleInputChange(value, 'scheduledTime')
                  }
                  placeholder={constants.TIME_PLACEHOLDER}
                  error={emailSchedulerError?.scheduledTime}
                  helperText={timeHelperText}
                />
              }
              marginRight="13px"
              marginBottom="0px"
            />
          </Grid>
          <Grid>
            <ReportCreateNewLabelField
              label={constants.EMAILS}
              component={
                <MultiEmailInputField
                  emails={emailSchedulerData?.toEmails}
                  handleEmailsChange={(emails: string[]) =>
                    handleInputChange(emails, 'toEmails')
                  }
                  placeholder={constants.EMAILS_PLACEHOLDER}
                  // getLabel={() => <></>}
                />
              }
              width="301px"
              marginRight="13px"
              marginBottom="0px"
            />
          </Grid>
          {showInfoText && (
            <Grid className={classes.textGrid}>
              {getInfoText(
                constants.INFO_LABEL.PART1 +
                  frequencyType +
                  constants.INFO_LABEL.PART2,
                false,
              )}
              {getInfoText(
                frequencyType === 'Custom'
                  ? moment(emailSchedulerData?.scheduledDate).format(
                      'Do MMM YYYY',
                    )
                  : moment(emailSchedulerData?.schedulerStartDate)
                      .date(Number(emailSchedulerData?.scheduledDay))
                      .format('Do MMM YYYY'),
                true,
              )}
              {getInfoText(constants.INFO_LABEL.PART3, false)}
              {getInfoText(
                moment(emailSchedulerData?.scheduledTime).format('h:mm A z'),
                true,
              )}
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

export default EmailScheduler;
